import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Card, Col, Row, Button, Spin } from 'antd';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './liveAds.scss';
import NoResults from '../../Components/NoResults/NoResults';
import FilterSort from '../../Components/Filter_Sort/Filter_Sort';
import DateService from '../../../Services/DateService';
import InfiniteScroll from 'react-infinite-scroll-component';
// import { getStatus } from '../../../Services/AdsStatusService'; // Removed as we're using new status mapping
import ProfileNotVerifiedPopup from '../../../Common/profileNotVerifiedPopup/ProfileNotVerifiedPopup';
import { toast } from 'react-toastify';
import useProfileCache from '../../../Common/Hooks/useProfileCache';
import useCacheInvalidation from '../../../Common/Hooks/useCacheInvalidation';
import AddIcon from '../../../assets/images/add_icon.png';
import { mapProfileStatus, getStatusDisplay, getStatusClass } from '../../../utils/statusMapping';



const LiveAds = ({ version, data, getStatusData, mobLiveAds }) => {
    document.title = "My Profiles";  
    let adminVerificationStatus = useSelector((state) => state.Dashboard?.adminVerificationStatus);
    const filterBox = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const currentLocation = location.pathname;

    // Use the caching hook
    const {
        profiles: ads,
        dashboardAds: dashboardActiveAds,
        loading,
        cacheStatus,
        fetchProfiles,
        clearCache,
        getCachedProfiles
    } = useProfileCache();

    // Use cache invalidation hook
    const {
        setupCacheInvalidation
    } = useCacheInvalidation();

    // Local state
    const [hasData, setHasData] = useState(false);
    const [absoluteTotal, setAbsoluteTotal] = useState(0); // Track total profiles regardless of filters - default to 0 instead of null
    const [filter, setFilter] = useState({
        filterOption: '',
        filterValue: ''
    });
    const [filterActive, setFilterActive] = useState(true);
    const [initialLoading, setInitialLoading] = useState(true);
    const [profileNotVerifiedModal] = useState(false);

    const limit = 6;
    const debounceTimeoutRef = useRef(null);
    const isApiCallInProgress = useRef(false);
    const isMountedRef = useRef(true);
    const lastPathRef = useRef(currentLocation);

    // Debounced filter handler
    const debouncedFilterHandler = useCallback(async (filterData) => {
        // Prevent multiple concurrent API calls
        if (isApiCallInProgress.current || !isMountedRef.current) {
            return;
        }

        // Clear existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Set new timeout
        debounceTimeoutRef.current = setTimeout(async () => {
            if (isApiCallInProgress.current || !isMountedRef.current) {
                return; // Prevent concurrent API calls or calls after unmount
            }

            try {
                isApiCallInProgress.current = true;

                // IMPORTANT: Clear cache before applying new filter
                clearCache();

                if (!filterData.filterOption && !filterData.filterValue) {
                    // No filter, fetch all profiles (no status parameter)
                    console.log('LiveAds Filter - clearing filters and fetching all profiles', { page: 1, limit });
                    await fetchProfiles({ 
                        page: 1, 
                        limit, 
                        forceRefresh: true,
                        filterState: { filterOption: '', filterValue: '' }
                    });
                } else {
                    // Apply filter - status will be in the filter if it's a status filter
                    const dataToSend = buildDataToSend('filter', filterData.filterOption, filterData.filterValue);
                    console.log('LiveAds Filter - applying filter and fetching profiles', { page: 1, limit, filters: dataToSend, filterState: filterData });
                    await fetchProfiles({
                        page: 1,
                        limit,
                        filters: dataToSend,
                        forceRefresh: true,
                        filterState: { filterOption: filterData.filterOption, filterValue: filterData.filterValue }
                    });
                }

            } catch (error) {
                console.error('Failed to fetch filtered profiles:', error);
            } finally {
                isApiCallInProgress.current = false;
            }
        }, 300); // 300ms debounce delay
    }, [fetchProfiles, limit, clearCache]);

    useEffect(() => {
        if (getStatusData) {
            getStatusData('active');
        }
    }, [getStatusData]);

    // Initialize data loading
    useEffect(() => {
        const initializeData = async () => {
            if (isApiCallInProgress.current) {
                return; // Prevent concurrent initialization
            }

            setInitialLoading(true);

            if (data && data.length > 0) {
                // Use provided data from dashboard (approved profiles)
                setHasData(true);
                setInitialLoading(false);
                return;
            }

            // Try to get cached data first
            const cached = getCachedProfiles();
            if (cached && cached.data && cached.data.length > 0) {
                setHasData(true);
                setInitialLoading(false);
                return;
            }

            // Only fetch if we don't have cached data and not already loading
            if (!loading && (!ads || ads.length === 0)) {
                try {
                    isApiCallInProgress.current = true;
                    await fetchProfiles({ page: 1, limit });
                    setHasData(true);
                } catch (error) {
                    console.error('Failed to fetch profiles:', error);
                } finally {
                    setInitialLoading(false);
                    isApiCallInProgress.current = false;
                }
            } else {
                setInitialLoading(false);
            }
        };

        // Only initialize once on mount or when data prop changes
        if (!hasData) {
            initializeData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, hasData]); // Add hasData to prevent multiple initializations

    // Force refresh data when navigating back to this page
    useEffect(() => {
        const refreshOnNavigation = async () => {
            // Check if we've navigated to this page (not initial mount)
            if (lastPathRef.current !== currentLocation && currentLocation !== '/dashboard') {
                lastPathRef.current = currentLocation;
                
                // Force refresh to get latest contacts_accessed data
                if (!isApiCallInProgress.current && isMountedRef.current) {
                    try {
                        isApiCallInProgress.current = true;
                        clearCache();
                        
                        const dataToSend = filter.filterOption && filter.filterValue 
                            ? buildDataToSend('filter', filter.filterOption, filter.filterValue)
                            : null;
                        
                        await fetchProfiles({
                            page: 1,
                            limit,
                            filters: dataToSend,
                            forceRefresh: true,
                            filterState: { filterOption: filter.filterOption, filterValue: filter.filterValue }
                        });
                    } catch (error) {
                        console.error('Failed to refresh data on navigation:', error);
                    } finally {
                        isApiCallInProgress.current = false;
                    }
                }
            } else {
                lastPathRef.current = currentLocation;
            }
        };

        refreshOnNavigation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocation]);

    // Handle filter changes with debouncing
    useEffect(() => {
        if (filter.filterOption || filter.filterValue) {
            debouncedFilterHandler(filter);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.filterOption, filter.filterValue]); // Remove debouncedFilterHandler from dependencies to prevent re-creation

    // Update hasData based on current data
    useEffect(() => {
        const adsArray = Array.isArray(ads) ? ads : [];

        if (adsArray.length > 0) {
            setHasData(true);
        }
    }, [ads]);

    // Track absolute total (total profiles without filters) for determining if user has any profiles at all
    useEffect(() => {
        // Update absoluteTotal in the following scenarios:
        // 1. When there's no active filter and we have a total from cache
        // 2. When absoluteTotal is still 0 and we have profiles data (initial load)
        // 3. When we have profiles in the ads array but absoluteTotal is 0
        
        if (!filter.filterOption && !filter.filterValue) {
            // No filter active - update from cache total
            if (cacheStatus?.total !== undefined) {
                setAbsoluteTotal(cacheStatus.total);
            }
        } else if (absoluteTotal === 0 && Array.isArray(ads) && ads.length > 0) {
            // Filter is active but absoluteTotal hasn't been set yet
            // This handles the case where user lands on page with a filter already applied
            // We know they have at least some profiles if we're showing filtered results
            setAbsoluteTotal(ads.length);
        }
    }, [cacheStatus?.total, filter.filterOption, filter.filterValue, ads, absoluteTotal]);


    const fetchMoreData = async () => {
        if (!loading && cacheStatus.hasMore && !isApiCallInProgress.current && isMountedRef.current) {
            try {
                isApiCallInProgress.current = true;
                console.log('LiveAds Pagination - fetching more profiles', {
                    currentPage: cacheStatus.currentPage,
                    nextPage: (cacheStatus.currentPage || 1) + 1,
                    hasMore: cacheStatus.hasMore
                });
                
                // Build filter data to send to API (only if filter is active)
                const dataToSend = filter.filterOption && filter.filterValue 
                    ? buildDataToSend('filter', filter.filterOption, filter.filterValue)
                    : null;
                
                // Load more profiles with current filter (no hardcoded status)
                await fetchProfiles({
                    page: (cacheStatus.currentPage || 1) + 1,
                    limit,
                    filters: dataToSend,
                    filterState: { filterOption: filter.filterOption, filterValue: filter.filterValue }
                });
                console.log('LiveAds Pagination - fetched next page successfully');
            } catch (error) {
                console.error('Failed to load more profiles:', error);
            } finally {
                isApiCallInProgress.current = false;
            }
        }
    };

    const featureStatus = (status) => {
        // Feature status handler
    }

    // Manual refresh function
    const handleRefresh = async () => {
        if (isApiCallInProgress.current || !isMountedRef.current) {
            return; // Prevent concurrent API calls
        }

        try {
            isApiCallInProgress.current = true;
            clearCache();
            
            const dataToSend = filter.filterOption && filter.filterValue 
                ? buildDataToSend('filter', filter.filterOption, filter.filterValue)
                : null;
            
            await fetchProfiles({
                page: 1,
                limit,
                filters: dataToSend,
                forceRefresh: true,
                filterState: { filterOption: filter.filterOption, filterValue: filter.filterValue }
            });
            if (isMountedRef.current) {
                toast.success('Data refreshed successfully');
            }
        } catch (error) {
            console.error('Failed to refresh data:', error);
            if (isMountedRef.current) {
                toast.error('Failed to refresh data');
            }
        } finally {
            isApiCallInProgress.current = false;
        }
    }

    function buildDataToSend(feature, option, value) {
        if (feature === 'filter') {
            if (option === 'transaction_status') {
                return {
                    'filter': {
                        'transaction_status': value,
                    }
                };
            } else if (option === 'status') {
                if (value === 'pending') {
                    return {
                        'filter': {
                            'status': value,
                            'transaction_status': 'completed'
                        }
                    };
                }
                else {
                    return {
                        'filter': {
                            'status': value,
                        }
                    };
                }
            }
        } else {
            return {
                'sort': {
                    sortOption: value
                }
            };
        }
    }

    // Initialize dashboard data and cache invalidation
    useEffect(() => {
        // Setup cache invalidation listeners
        const cleanup = setupCacheInvalidation();

        const handleClickOutside = (event) => {
            if (filterBox.current && !filterBox.current.contains(event.target)) {
                setFilterActive(prev => !prev);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            cleanup();
        };
    }, [setupCacheInvalidation]);

    useEffect(() => {
        if (adminVerificationStatus) {
            // Admin verification status updated
        }
    }, [adminVerificationStatus])

    // Cleanup effect to clear debounce timeout on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            isApiCallInProgress.current = false;
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!loading) {
            setInitialLoading(false);
        }
    }, [loading]);

    useEffect(() => {
        console.log('profileNotVerifiedModal', profileNotVerifiedModal)
    }, [profileNotVerifiedModal])

    // Determine if filter should be shown
    // Hide filter if user has no profiles at all (absoluteTotal === 0)
    // Show filter if user has profiles, regardless of current filtered results
    // Also show if we have ads loaded (handles timing issues)
    const shouldShowFilter = location.pathname !== '/dashboard' && 
                             (absoluteTotal > 0 || (Array.isArray(ads) && ads.length > 0));

    // Check if filter is currently active
    const isFilterActive = !!(filter.filterOption && filter.filterValue);

    // Debug logging for ads data
    useEffect(() => {
        console.log('LiveAds Debug - ads:', ads);
        console.log('LiveAds Debug - ads length:', ads?.length);
        console.log('LiveAds Debug - ads type:', typeof ads);
        console.log('LiveAds Debug - cacheStatus:', cacheStatus);
        console.log('LiveAds Debug - loading:', loading);
        console.log('LiveAds Debug - hasData:', hasData);
        console.log('LiveAds Debug - absoluteTotal:', absoluteTotal);
        console.log('LiveAds Debug - isFilterActive:', isFilterActive);
        console.log('LiveAds Debug - shouldShowFilter:', shouldShowFilter);
        console.log('LiveAds Debug - filter:', filter);
    }, [ads, cacheStatus, loading, hasData, absoluteTotal, isFilterActive, shouldShowFilter, filter])

    // Trace which list is used for rendering and key identifiers
    useEffect(() => {
        const source = currentLocation !== '/dashboard' ? 'ads' : 'dashboardActiveAds';
        const list = currentLocation !== '/dashboard' ? (Array.isArray(ads) ? ads : []) : (Array.isArray(dashboardActiveAds) ? dashboardActiveAds : []);
        const ids = list.map(item => item?.id);
        console.log('LiveAds Render - source list:', source, '| count:', list.length, '| ids:', ids);
    }, [ads, dashboardActiveAds, currentLocation]);

    // Derive the list used for rendering on every render cycle and log its length
    const listForRender = currentLocation !== '/dashboard'
        ? (Array.isArray(ads) ? ads : [])
        : (Array.isArray(dashboardActiveAds) ? dashboardActiveAds : []);
    const totalProfilesCount = listForRender.length;
    console.info('[LiveAds] Render count:', totalProfilesCount);
    try {
        console.table(listForRender.map((item, idx) => ({ idx, id: item?.id, status: item?.status })));
    } catch (_) {
        // ignore console.table errors in older browsers/environments
    }

    return (
        <React.Fragment >
            {shouldShowFilter && (
                <div className="filter_parent" ref={filterBox}>
                    <FilterSort
                        featureStatus={featureStatus}
                        filterDropdownStatus={filterActive}
                        filterCatch={(filterType, key) => {
                            setFilter({
                                filterOption: filterType,
                                filterValue: key,
                            });
                        }}
                    />
                    <Button className='new_profile_btn lap_display' type='primary' onClick={() => {
                        navigate('/profile-creation');
                    }}>
                        Add New Profile
                    </Button>
                </div>
            )}

            <div className="live_ads_bod parent_wrap">
                {
                    (loading && initialLoading) && (
                        // <ShimmerSimpleGallery card imageHeight={180} caption className="basic_shimmer_holder" />
                        <div className="spinner center" key={0}>
                            <Spin />
                        </div>
                    )
                }
                { 
                    shouldShowFilter && (
                        <Button className='new_profile_btn desktop_display' type='primary' onClick={() => {
                            navigate('/profile-creation');
                        }}>
                            Add New Profile
                        </Button>
                    )
                }
               

                <InfiniteScroll
                    dataLength={Array.isArray(ads) ? ads.length : 0}
                    next={fetchMoreData}
                    hasMore={cacheStatus.hasMore}
                    loader={
                        loading && (
                            <div className="spinner center" key={0}>
                                <Spin />
                            </div>
                        )
                    }


                >
                    <Col lg={24} xs={24} style={{ marginTop: 0 }}>

                        <section className={`live_ads block ${version === 'Detail' ? 'detailVersion' : 'defaultVersion'}`} style={{ width: "100%" }}>
                            {/*  live ads title added  */}
                            {/* {dashboardActiveAds.length !== 0 && currentLocation === '/dashboard' && (
                                <h4 className="text_md mb-0" style={{ marginBottom: version === 'Detail' ? null : "1em", fontWeight: '500' }}>
                                    Live Ads
                                </h4>
                            )} */}

                            <Card>
                                {(currentLocation === '/dashboard' && !ads?.length > 0) && (
                                    <NoResults 
                                        className='dashboard_no_ad'
                                        type={'ad'}
                                        title={'Add a Profile'}
                                        description={'Get started on Lumi Bureau by adding a profile!'}>
                                        <Button className="wide mb icon_btn" type='primary'
                                            onClick={() => {
                                                // if (!status) {
                                                //     setProfileNotVerifiedModal(prev => !prev)
                                                // } else {
                                                //     navigate('/profile-creation');
                                                // }
                                                navigate('/profile-creation');
                                            }}>Add New Profile <img src={AddIcon} alt="Add"  /></Button>
                                    </NoResults>
                                )}


                                {
                                    <div className="button_wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>

                                        {version === 'Detail' ? (
                                            <>
                                                <h4 className="text_md mb-0" style={{ marginBottom: version === 'Detail' ? null : "1em", fontWeight: '500' }}>
                                                    {version === 'Detail' ? `All Ads (${totalProfilesCount})` : 'Live Ads'}
                                                </h4>
                                                {shouldShowFilter && (
                                                    <div className="filter_parent" ref={filterBox} style={{ display: 'inline-flex' }}>
                                                        <FilterSort
                                                            featureStatus={featureStatus}
                                                            filterDropdownStatus={filterActive}
                                                            filterCatch={(filterType, key) => {
                                                                setFilter({
                                                                    filterOption: filterType,
                                                                    filterValue: key,
                                                                });
                                                            }}
                                                        />
                                                        <span className="text_sm" style={{ marginLeft: 12 }}>{`Count: ${totalProfilesCount}`}</span>
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <Button
                                                        onClick={handleRefresh}
                                                        loading={loading}
                                                        icon="🔄"
                                                    >
                                                        Refresh
                                                    </Button>
                                                    <Link type='primary' to="/profile-creation">
                                                        <Button type='primary'>Create Ad</Button>
                                                    </Link>
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                }
                                {/* {
                                    ads.length > 0 && currentLocation === '/dashboard' && (
                                        <h4 className="text_md" style={{ marginBottom: '1em' }}>
                                            {ads.length > 1 ? 'Approved Profiles' : 'Approved Profile'}
                                        </h4>
                                    )
                                } */}
                                <Row className={`live_ads_list`} gutter={[35, 25]}>
                                    {(Array.isArray(ads) && ads.length > 0) || (Array.isArray(dashboardActiveAds) && dashboardActiveAds.length > 0) ? (
                                        <>
                                            {currentLocation !== '/dashboard' && (
                                                <>
                                                    {Array.isArray(ads) && ads.map((item, index) => (
                                                        <Col xs={24} sm={12} lg={8} key={index} className='live_ads_cnt '>
                                                            <div className="red_shadow" style={{ height: "100%" }}>
                                                                {/* <Link to={`/live-ads/${item.id}`} className={`${!status && 'inactive_link'}`}> */}
                                                                <Link to={`/live-ads/${item.id}`} >
                                                                    <figure style={{ marginBottom: "0" }} className="banner_ratio">
                                                                        {item?.profile_images
                                                                            ?.filter(img => img.is_profile)
                                                                            ?.map((img, imgIndex) => (
                                                                                <img
                                                                                    key={imgIndex}
                                                                                    src={img.thumbnail}
                                                                                    alt={`Profile ${imgIndex + 1}`}
                                                                                    className="img_full cover"
                                                                                    loading="lazy"
                                                                                />
                                                                            ))}
                                                                    </figure>
                                                                </Link>
                                                                <div className='ad_content'>
                                                                    {version === "Detail" ? (
                                                                        <div className="cnt">
                                                                            <div className="bottom_wrap">
                                                                                <div className="top">
                                                                                    <span>
                                                                                        <span className='semibold'>Created Date :</span>&nbsp;
                                                                                        <span>{DateService.formatDateCustom(item?.created_at)}</span>
                                                                                    </span>
                                                                                    <span><strong className='text_red'>Ad ID</strong> : {item?.ad_id}</span>
                                                                                </div>
                                                                                <div className="bottom">
                                                                                    <span>
                                                                                        <span>Reach Progress :</span>&nbsp;
                                                                                        <span>{Math.round(item?.reach_progress) ? Math.round(item?.reach_progress) + '%' : 0 + "%"}</span>
                                                                                    </span>

                                                                                    {item?.transaction_status !== "completed" && (
                                                                                        <Button className="wide text_white" type='primary' onClick={() => {
                                                                                            navigate('/profile-creation', { state: { adItem: item, initiatePaymentStatus: true } });
                                                                                        }}>
                                                                                            <span className='text_white'>Continue Payment</span>
                                                                                        </Button>
                                                                                    )}

                                                                                    {item.contacts_accessed && (
                                                                                        <span className='date'>
                                                                                            <span >Contacts Accessed: <span className="text_red" style={{ fontWeight: "bold" }}>{item.contactsAccessed}</span></span>
                                                                                        </span>
                                                                                    )}

                                                                                </div>
                                                                                <div className="bottom">
                                                                                    <span>
                                                                                        <span>Clicks :</span>&nbsp;
                                                                                        <span>{(item?.status !== 'active' && item?.total_clicks === 0) ? 'NA' : item?.total_clicks}</span>
                                                                                    </span>
                                                                                    <span className='date'>
                                                                                        <span>Status : </span>
                                                                                        {getStatusDisplay(item?.status)}
                                                                                    </span>
                                                                                    <Link to={`/live-ads/${item.id}`} className='btn_blue text_sm text_blue'>View Details</Link>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="cnt default_live_ads">
                                                                            <span className='date text_red md' style={{ fontWeight: 400 }}>
                                                                                <span>Status :</span>&nbsp;
                                                                                <span className={`${getStatusClass(mapProfileStatus(item.status))} semibold`}>{mapProfileStatus(item.status)}</span>
                                                                            </span>
                                                                            <span className='md'>
                                                                                <span>Views :</span>&nbsp;
                                                                                <span className='semibold'>{item.views}</span>
                                                                            </span>
                                                                            <span className='md'>
                                                                                <span>Clicks :</span>&nbsp;
                                                                                <span className='semibold'>{item.clicks}</span>
                                                                            </span>

                                                                            <span className='date text_red md' style={{ fontWeight: 400 }}>
                                                                                <span>Interests Received :</span>&nbsp;
                                                                                <span className='semibold'>{item.enquiries}</span>
                                                                            </span>
                                                                            <span className='date text_red md' style={{ fontWeight: 400 }}>
                                                                                <span>Contacts Accessed :</span>&nbsp;
                                                                                <span className='semibold text_red'>{item.contacts_accessed}</span>
                                                                            </span>
                                                                            <Link to={`/live-ads/${item.id}`} className='btn_blue text_sm text_blue details_btn'>View Details</Link>
                                                                        </div>
                                                                    )}
                                                                    {/* {
                                                                        item?.status === "rejected" && (
                                                                            <div className="rejected_cnt">
                                                                                <div>
                                                                                    {item?.warning?.title && <h2 className="text_lg text_red">{item?.warning?.title}</h2>}
                                                                                    {item?.warning?.description && <p className='md text_red'>{item?.warning?.description}</p>}
                                                                                </div>
                                                                                <Button className="cmn text_white" type='primary' onClick={() => {
                                                                                    navigate('/profile-creation', { state: { adItem: item, initiatePaymentStatus: true } });
                                                                                }}>
                                                                                    <span className='text_white md'>Edit Ad</span>
                                                                                </Button>
                                                                            </div>
                                                                        )
                                                                    } */}
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    ))}
                                                </>
                                            )}



                                        </>
                                    ) : (
                                        (!loading && currentLocation !== '/dashboard') && (
                                            <Col xs={24} style={{ width: '100%' }}>
                                                {isFilterActive ? (
                                                    // When filter is active but no results
                                                    <NoResults
                                                        type={'ad'}
                                                        title={'No Results Found'}
                                                        description={'No profiles match your current filter.'}>
                                                        {/* <Button className="wide mb" type='default'
                                                            onClick={() => {
                                                                setFilter({
                                                                    filterOption: '',
                                                                    filterValue: ''
                                                                });
                                                            }}>Clear Filter</Button> */}
                                                    </NoResults>
                                                ) : (
                                                    // When no filter is active and no profiles exist
                                                    <NoResults
                                                        type={'ad'}
                                                        title={'No Profiles Available'}
                                                        description={'Get started on Lumi Bureau by adding your first profile!'}>
                                                        <Button className="wide mb icon_btn" type='primary'
                                                            onClick={() => {
                                                                navigate('/profile-creation');
                                                            }}>Add New Profile <img src={AddIcon} alt="Add" /></Button>
                                                    </NoResults>
                                                )}
                                            </Col>
                                        )
                                    )}

                                </Row>
                                {/* No separate no-result banner; handled by NoResults above */}
                                <ProfileNotVerifiedPopup status={profileNotVerifiedModal} />
                            </Card>
                        </section>
                    </Col>
                </InfiniteScroll>
            </div>
        </React.Fragment>
    );
};

export default LiveAds;
